import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { API } from '@/services/api.service';
import { retrieveAllCustomersNew } from '@/services/customers.service';
import TimeTrackingService from '@/services/timeTracking.service';

// Fetches only companies with timeTracking enabled
export const useTimeTrackingCompanies = () => {
  return useQuery({
    queryKey: ['companies-dropdown', 'timeTracking', 'ownEmployer'],
    queryFn: () =>
      API.companies.retrieveDropdownAllCompanies({
        // Employees track their own time in the company they work in.
        filter: {
          'applicationSettings.timeTracking': true,
          includeOwnEmployer: true,
        },
      }),
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    select: (data) => {
      const { isSingleCompany, items } = data || {};
      const options = (items || []).map((c) => ({
        value: c._id,
        label: c.name,
      }));
      return {
        isSingle: isSingleCompany || options.length === 1,
        options,
      };
    },
  });
};

/**
 * Drops a company selection that is not among the companies the caller may
 * pick.
 *
 * The choice is persisted in localStorage, so it outlives the session that made
 * it: signing in as somebody else in the same browser restored their company,
 * and since the picker never lists it, antd had no label to show and printed
 * the raw ObjectId instead. The request behind it was refused anyway — the
 * screen just could not say why.
 *
 * @param {Object|undefined} companyData - Result of useTimeTrackingCompanies.
 * @param {string} selected - Currently selected company id.
 * @param {Function} clear - Called once to drop the selection.
 */
export const useDropUnavailableCompany = (companyData, selected, clear) => {
  useEffect(() => {
    if (!companyData || !selected) return;

    const allowed = (companyData.options || []).map((o) => String(o.value));

    if (!allowed.includes(String(selected))) clear();
  }, [companyData, selected, clear]);
};

export const useCustomersForCompany = (companyId) => {
  return useQuery({
    queryKey: ['customers', 'dropdown', companyId],
    queryFn: () =>
      retrieveAllCustomersNew({
        company: companyId,
        limit: 500,
        offset: 1,
        select: { customerName: 1 },
      }),
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    enabled: !!companyId,
    select: (data) =>
      (data?.items || []).map((c) => ({ _id: c._id, name: c.customerName })),
  });
};

export const TIME_TRACKING_KEYS = {
  projects: (params) => ['time-tracking', 'projects', params],
  project: (id) => ['time-tracking', 'project', id],
  projectMembers: (id, params) => [
    'time-tracking',
    'project-members',
    id,
    params,
  ],
  projectTasks: (id, params) => ['time-tracking', 'project-tasks', id, params],
  entries: (params) => ['time-tracking', 'entries', params],
  monthlyData: (params) => ['time-tracking', 'monthly', params],
  pending: (params) => ['time-tracking', 'pending', params],
  projectStructure: (companyId) => [
    'time-tracking',
    'project-structure',
    companyId,
  ],
};

export const useProjects = (params = {}, options = {}) => {
  return useQuery({
    queryKey: TIME_TRACKING_KEYS.projects(params),
    queryFn: () => TimeTrackingService.getProjects(params),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    ...options,
  });
};

export const useProject = (id, options = {}) => {
  return useQuery({
    queryKey: TIME_TRACKING_KEYS.project(id),
    queryFn: () => TimeTrackingService.getProjectById(id),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    enabled: !!id,
    ...options,
  });
};

export const useProjectMembers = (id, params = {}, options = {}) => {
  return useQuery({
    queryKey: TIME_TRACKING_KEYS.projectMembers(id, params),
    queryFn: () => TimeTrackingService.getProjectMembers(id, params),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    enabled: !!id,
    ...options,
  });
};

export const useProjectTasks = (id, params = {}, options = {}) => {
  return useQuery({
    queryKey: TIME_TRACKING_KEYS.projectTasks(id, params),
    queryFn: () => TimeTrackingService.getProjectTasks(id, params),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    enabled: !!id,
    ...options,
  });
};

export const useTimeEntries = (params = {}, options = {}) => {
  return useQuery({
    queryKey: TIME_TRACKING_KEYS.entries(params),
    queryFn: () => TimeTrackingService.getTimeEntries(params),
    staleTime: 30_000,
    ...options,
  });
};

export const useMonthlyTimeData = (params = {}, options = {}) => {
  return useQuery({
    queryKey: TIME_TRACKING_KEYS.monthlyData(params),
    queryFn: () => TimeTrackingService.getMonthlyTimeData(params),
    staleTime: 30_000,
    ...options,
  });
};

export const useCreateProject = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => TimeTrackingService.createProject(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['time-tracking', 'projects'],
      });
    },
  });
};

export const useUpdateProject = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => TimeTrackingService.updateProject(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['time-tracking', 'projects'],
      });
      queryClient.invalidateQueries({ queryKey: ['time-tracking', 'project'] });
    },
  });
};

export const useDeleteProject = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => TimeTrackingService.deleteProject(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['time-tracking', 'projects'],
      });
    },
  });
};

export const useProjectStructure = (companyId, open) => {
  return useQuery({
    queryKey: TIME_TRACKING_KEYS.projectStructure(companyId),
    queryFn: () =>
      TimeTrackingService.getProjectStructure({ company: companyId }),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    enabled: !!companyId && open,
  });
};

export const useReassignTaskMembers = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => TimeTrackingService.reassignTaskMembers(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['time-tracking', 'project-structure'],
      });
      queryClient.invalidateQueries({
        queryKey: ['time-tracking', 'projects'],
      });
      queryClient.invalidateQueries({
        queryKey: ['time-tracking', 'project'],
      });
    },
  });
};

export const useCreateTimeEntry = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => TimeTrackingService.createTimeEntry(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['time-tracking', 'entries'] });
      queryClient.invalidateQueries({ queryKey: ['time-tracking', 'monthly'] });
    },
  });
};

export const useUpdateTimeEntry = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => TimeTrackingService.updateTimeEntry(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['time-tracking', 'entries'] });
      queryClient.invalidateQueries({ queryKey: ['time-tracking', 'monthly'] });
    },
  });
};

export const useDeleteTimeEntry = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => TimeTrackingService.deleteTimeEntry(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['time-tracking', 'entries'] });
      queryClient.invalidateQueries({ queryKey: ['time-tracking', 'monthly'] });
    },
  });
};

export const useAddTask = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, data }) =>
      TimeTrackingService.addTask(projectId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['time-tracking', 'project'] });
      queryClient.invalidateQueries({
        queryKey: ['time-tracking', 'project-tasks'],
      });
      queryClient.invalidateQueries({
        queryKey: ['time-tracking', 'projects'],
      });
    },
  });
};

export const useUpdateTask = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, taskId, data }) =>
      TimeTrackingService.updateTask(projectId, taskId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['time-tracking', 'project'] });
      queryClient.invalidateQueries({
        queryKey: ['time-tracking', 'project-tasks'],
      });
      queryClient.invalidateQueries({
        queryKey: ['time-tracking', 'projects'],
      });
    },
  });
};

export const useDeleteTask = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, taskId }) =>
      TimeTrackingService.deleteTask(projectId, taskId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['time-tracking', 'project'] });
      queryClient.invalidateQueries({
        queryKey: ['time-tracking', 'project-tasks'],
      });
      queryClient.invalidateQueries({
        queryKey: ['time-tracking', 'projects'],
      });
    },
  });
};

export const useAddMember = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, data }) =>
      TimeTrackingService.addMember(projectId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['time-tracking', 'project'] });
      queryClient.invalidateQueries({
        queryKey: ['time-tracking', 'project-members'],
      });
      queryClient.invalidateQueries({
        queryKey: ['time-tracking', 'projects'],
      });
    },
  });
};

export const useUpdateMember = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, employeeId, data }) =>
      TimeTrackingService.updateMember(projectId, employeeId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['time-tracking', 'project'] });
      queryClient.invalidateQueries({
        queryKey: ['time-tracking', 'project-members'],
      });
      queryClient.invalidateQueries({
        queryKey: ['time-tracking', 'projects'],
      });
    },
  });
};

export const useRemoveMember = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, employeeId }) =>
      TimeTrackingService.removeMember(projectId, employeeId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['time-tracking', 'project'] });
      queryClient.invalidateQueries({
        queryKey: ['time-tracking', 'project-members'],
      });
      queryClient.invalidateQueries({
        queryKey: ['time-tracking', 'projects'],
      });
    },
  });
};

// Approval hooks
export const usePendingEntries = (params = {}, options = {}) => {
  return useQuery({
    queryKey: TIME_TRACKING_KEYS.pending(params),
    queryFn: () => TimeTrackingService.getPendingEntries(params),
    staleTime: 15_000,
    ...options,
  });
};

export const useSubmitEntries = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => TimeTrackingService.submitEntries(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['time-tracking', 'entries'] });
      queryClient.invalidateQueries({ queryKey: ['time-tracking', 'monthly'] });
      queryClient.invalidateQueries({ queryKey: ['time-tracking', 'pending'] });
    },
  });
};

export const useApproveEntries = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => TimeTrackingService.approveEntries(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['time-tracking', 'pending'] });
      queryClient.invalidateQueries({ queryKey: ['time-tracking', 'entries'] });
      queryClient.invalidateQueries({ queryKey: ['time-tracking', 'monthly'] });
    },
  });
};

export const useCopyEntries = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => TimeTrackingService.copyEntries(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['time-tracking', 'entries'] });
      queryClient.invalidateQueries({ queryKey: ['time-tracking', 'monthly'] });
    },
  });
};

export const useSyncToPayroll = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => TimeTrackingService.syncToPayroll(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['time-tracking', 'entries'] });
      queryClient.invalidateQueries({ queryKey: ['time-tracking', 'monthly'] });
      queryClient.invalidateQueries({ queryKey: ['employee-salary'] });
    },
  });
};

export const useRejectEntries = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => TimeTrackingService.rejectEntries(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['time-tracking', 'pending'] });
      queryClient.invalidateQueries({ queryKey: ['time-tracking', 'entries'] });
      queryClient.invalidateQueries({ queryKey: ['time-tracking', 'monthly'] });
    },
  });
};

// Customer stats hook
export const useCustomerTimeStats = (customerId, params = {}, options = {}) => {
  return useQuery({
    queryKey: ['time-tracking', 'customer-stats', customerId, params],
    queryFn: () =>
      TimeTrackingService.getReportByCustomerId(customerId, params),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    enabled: !!customerId && !!params.company,
    ...options,
  });
};

// Report hooks
export const useReportByProject = (params = {}, options = {}) => {
  return useQuery({
    queryKey: ['time-tracking', 'report', 'by-project', params],
    queryFn: () => TimeTrackingService.getReportByProject(params),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    enabled: !!params.company,
    ...options,
  });
};

export const useReportByEmployee = (params = {}, options = {}) => {
  return useQuery({
    queryKey: ['time-tracking', 'report', 'by-employee', params],
    queryFn: () => TimeTrackingService.getReportByEmployee(params),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    enabled: !!params.company,
    ...options,
  });
};

export const useReportByClient = (params = {}, options = {}) => {
  return useQuery({
    queryKey: ['time-tracking', 'report', 'by-client', params],
    queryFn: () => TimeTrackingService.getReportByClient(params),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    enabled: !!params.company,
    ...options,
  });
};

export const useReportEntries = (params = {}, options = {}) => {
  return useQuery({
    queryKey: ['time-tracking', 'report', 'entries', params],
    queryFn: () => TimeTrackingService.getReportEntries(params),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    enabled: !!params.company && !!params.groupId,
    ...options,
  });
};

export const useReportSummary = (params = {}, options = {}) => {
  return useQuery({
    queryKey: ['time-tracking', 'report', 'summary', params],
    queryFn: () => TimeTrackingService.getReportSummary(params),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    enabled: !!params.company,
    ...options,
  });
};

export const useReportAnalytics = (params = {}, options = {}) => {
  return useQuery({
    queryKey: ['time-tracking', 'report', 'analytics', params],
    queryFn: () => TimeTrackingService.getReportAnalytics(params),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    enabled: !!params.company,
    ...options,
  });
};
